import type en from '../en'
import type { DeepPartial } from '../../merge'

/**
 * samly.ge copy that differs from the Uzbek site. Everything else comes from ../en.
 * Claims here must match what Samly really offers in Georgia.
 */
const overrides: DeepPartial<typeof en> = {
  hero: {
    stat_country: 'Now in Georgia',
    stat_payments: 'Cash · card · QR',
    dashboard: {
      url: 'samly.ge/dashboard',
      currency: 'GEL',
    },
  },
  logos: {
    formats: {
      oshkhona: 'Khinkali houses',
    },
  },
  painpoints: {
    before: [
      'You raise a price at the POS — the delivery storefront still shows the old one',
      'The khachapuri ran out, but orders for it keep coming in',
      'Stock in Excel, POS on its own, delivery on an aggregator',
      "A waiter promises a guest a dish the kitchen doesn't have",
      'Building one report means reconciling three exports by hand',
    ],
  },
  onemenu: {
    moments: [
      {
        title: 'Change a price once',
        desc: 'The new price is live at the POS, in the QR menu, on your delivery storefront, in the waiter app and on the screens. Nothing to duplicate.',
      },
      {
        title: 'Sell the last portion',
        desc: 'The dish hits the stop list that same second: it disappears from the storefront, the guest stops seeing it, and the waiter can no longer ring it up.',
      },
      {
        title: 'Stock counts ingredients',
        desc: 'Every dish has a recipe. Sell a khachapuri and the flour, cheese and matsoni leave the warehouse. Your stock figure is real, not an estimate.',
      },
    ],
  },
  stats: {
    items: [
      { val: '20+', label: 'venues', sub: 'running on Samly' },
      { val: '1 month', label: 'free', sub: 'full set, no card' },
      { val: '8', label: 'components', sub: 'pay only for yours' },
      { val: '−30%', label: 'paying yearly', sub: '3-month minimum term' },
    ],
  },
  features: {
    mock: {
      warehouse_sold: '1 khachapuri sold',
      currency: 'GEL',
      pay_click: 'Pay by card',
      pay_payme: 'Call the waiter',
      dishes: {
        plov: 'Khachapuri',
        lagman: 'Khinkali',
        tea: 'Tea',
        shashlik: 'Mtsvadi',
        manti: 'Lobiani',
        soup: 'Kharcho',
        salad: 'Salad',
      },
      ingredients: {
        rice: 'Flour',
        oil: 'Matsoni',
        meat: 'Sulguni',
        carrot: 'Eggs',
      },
    },
    pos: {
      points: [
        'Cash, card and QR in one window',
        'Refunds, discounts and deletions need a manager PIN',
        'Shifts and cash discrepancies stay under control',
        'Receipts on any ESC/POS or StarPRNT printer',
        'Works offline and syncs afterwards',
      ],
    },
    storefront: {
      points: [
        'Telegram Mini App, website and mobile app',
        'Your own domain — guests see your brand, not a marketplace',
        'Cash or card on delivery',
        'Orders land straight in the POS and the kitchen',
        'No aggregator commission on your own orders',
      ],
    },
    qr: {
      points: [
        'A QR code on the table opens the menu in a browser — no app',
        'One shared cart for the whole table',
        'The order goes straight to the kitchen',
        'The same menu and the same stock as the POS',
        'Guests track order status instead of asking',
      ],
    },
    warehouse: {
      points: [
        'A recipe card for every dish',
        'Sell a khachapuri and the flour, cheese and matsoni are deducted',
        'Cost and margin per menu item',
        'The stop list builds itself the moment an ingredient runs out',
        'Several warehouses, if you run more than one kitchen',
      ],
    },
  },
  integrations: {
    subtitle: 'Payments, delivery and hardware — out of the box.',
    groups: {
      // No Georgian partner is signed yet (29.09.2026): all of them carry the `soon` badge. Drop it once a deal is live.
      payment: {
        title: 'Payments',
        items: [
          { name: 'Cash, card, QR', desc: 'Any bank terminal at the register' },
          { name: 'TBC Pay', logo: '/partners/tbc.svg', desc: 'Planned', soon: true },
          { name: 'Bank of Georgia', logo: '/partners/bank-of-georgia.svg', desc: 'Planned', soon: true },
        ],
      },
      delivery: {
        title: 'Delivery',
        items: [
          { name: 'Own storefront', desc: 'Telegram, web, mobile app' },
          { name: 'Wolt', logo: '/partners/wolt.png', desc: 'Planned', soon: true },
          { name: 'Glovo', logo: '/partners/glovo.png', desc: 'Planned', soon: true },
        ],
      },
      local: {
        title: 'Platform',
        items: [
          { name: 'Android / iOS', desc: 'Any device' },
          { name: 'Open API', desc: 'Your own integrations, included' },
        ],
      },
    },
  },
  dashboardPreview: {
    owner: {
      mockup: {
        revenue: 'GEL',
        branches: {
          chilanzar: 'Vake',
          yunusabad: 'Saburtalo',
          samarkand: 'Batumi',
        },
      },
    },
    qr: {
      features: {
        payments: 'The order reaches the kitchen in seconds',
      },
    },
    monitors: {
      mockup: {
        items: {
          lagman: 'Khinkali',
          plov: 'Khachapuri',
          shashlik: 'Mtsvadi',
          manti: 'Lobiani',
        },
      },
    },
  },
  components: {
    paid_note: 'Prices are monthly. Own delivery, the waiter app and the QR menu are bought per branch.',
    per_month: 'GEL/mo',
  },
  pricing: {
    subtitle: 'The price is the sum of your components. Minimum term is 3 months.',
    per_month: 'GEL/mo',
    receipt_total_sub: 'GEL for {{months}} months',
    rules: [
      'The price is the sum of your components. There are no plans.',
      'The minimum purchase term is 3 months.',
      'Term discount: 3 months −10%, 6 months −20%, 12 months −30%.',
      'The total is always rounded down.',
      'Minimum: 1 branch, 1 employee, 1 POS device.',
      'Own delivery, the waiter app and the QR menu are per branch and can never exceed your branch count.',
    ],
  },
  faq: {
    items: [
      {
        q: 'How is the price calculated?',
        a: 'There are no plans. You assemble a set from eight components and the price is their sum. The minimum purchase term is 3 months, and the term discount is 3 months −10%, 6 months −20%, 12 months −30%. The minimum set is 1 branch, 1 employee and 1 POS device.',
      },
      {
        q: 'What is included at no extra cost?',
        a: 'Kitchen (KDS), the manager app, loyalty and CRM, AI analytics, the self-service kiosk, Open API, the stop list, shifts and Z-reports, the floor plan, PIN-protected refunds, owner push alerts and receipt printing. You only pay for branches, employees, POS devices, own delivery, the waiter app, the QR menu, screens and warehouses.',
      },
      {
        q: 'Does the warehouse really count ingredients rather than portions?',
        a: 'Yes. Every dish has a recipe card. When you sell a khachapuri, the flour, cheese and matsoni are deducted from stock in the quantities you specified. That is why the stock figure is real, the cost is calculated for you, and the stop list builds itself the moment any ingredient runs out.',
      },
      {
        q: 'How does own delivery work?',
        a: 'You get your own storefront: a Telegram Mini App, a website and a mobile app, on your own domain if you want one. Orders land straight in the POS and the kitchen, on the same menu and the same stock. You pay no aggregator commission on those orders.',
      },
      {
        q: 'How long does rollout take?',
        a: 'Up to a week. Our team does the setup: we migrate the menu, build the recipe cards, connect the hardware and train your staff. Onboarding is free.',
      },
      {
        q: 'Do I need to buy hardware?',
        a: "If you don't already have it, yes. A monoblock for the register, a receipt printer and screens for the kitchen and menu are bought separately. It's a one-off cost and the amounts vary a great deal, which is why it isn't in the subscription. We'll tell you exactly what your room needs and spec it to your budget. Samly runs on Android and iOS, so an ordinary tablet covers part of it.",
      },
      {
        q: 'Does it work without internet?',
        a: 'The POS and the kitchen screen work offline. Menu screens can play content from a USB drive. When the connection returns, data syncs by itself — no order is lost.',
      },
      {
        q: 'What does the free month include?',
        a: 'The full set of components for a month, with no card and no commitment. If you stay on after that, the 3-month minimum term applies.',
      },
      {
        // TODO(ge): the Samly apps have no Georgian UI yet (ru/uz/en only). Update once it ships.
        q: 'Which languages does Samly support?',
        a: 'The Samly apps are available in Russian and English. Support answers in Russian and English by phone and on Telegram, daily except Sunday, 09:00–21:00 Tbilisi time.',
      },
    ],
  },
  cta: {
    trust_payment: 'Cash · card · QR',
    trust_local: 'Now in Georgia',
  },
  contact: {
    hours: 'Mon–Sat, 09:00–21:00 (Tbilisi time)',
  },
}

export default overrides
